/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2020, 2023
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

import _ from 'lodash';
import { createSelector } from 'reselect';

import { Variable } from '../../../../ts/action';
import { RootState } from '../../../../ts/state';

export const getVariables = (state: RootState): Record<string, Variable> =>
  state?.models.variables;
const getSystemVariables = (state: RootState) => state?.models.variables_system;
export const getAllSystemVariables = state =>
  Object.values(getSystemVariables(state));

export const getGlobalVariables = createSelector([getVariables], variables =>
  variables ? Object.values(variables) : []
);

export const getIntegrationsVariables = createSelector(
  [getSystemVariables],
  variables =>
    variables
      ? Object.values(
          _.filter(variables, variable => variable.integrationVariable)
        )
      : []
);

export const getAssistantVariables = createSelector(
  [getSystemVariables],
  variables =>
    variables
      ? Object.values(
          _.filter(variables, variable => !variable.integrationVariable)
        )
      : []
);

export const getReadWriteAssistantVariables = createSelector(
  [getSystemVariables],
  _variables => {
    /*
    removed awaiting outcome of chat here https://ibm-analytics.slack.com/archives/CTM0B2N65/p1645674850541549
        return _.filter(variables, variable => !variable.read_only)

    Note that the code incorrectly assumes that "variables" is an array, when it is an object whose values
    are an array (see implementation of getAssistantVariables()).
    */
    return [];
  }
);

export const getVariable = (
  state: RootState,
  variableId: string
): Variable | null => {
  return _.get(state, 'models.variables', {})[variableId] || null;
};

export const selectLastModifiedVariableTime = createSelector(
  [getGlobalVariables],
  variables => _.maxBy(variables, 'updated')?.updated
);

export const selectLastModifiedVariable = createSelector(
  [getGlobalVariables],
  variables => _.maxBy(variables, 'updated')
);

export const getVariablesForCalloutFromDialog = createSelector(
  [getGlobalVariables, getReadWriteAssistantVariables],
  (globalVariables, _assistantVariables) => {
    /*
    removed awaiting outcome of chat here https://ibm-analytics.slack.com/archives/CTM0B2N65/p1645674850541549
    const allVariables = [...globalVariables, ...assistantVariables];
    const concatVariables = _.map(allVariables, singleVariable => {
      let { variable } = singleVariable;
      if (singleVariable.system_variable) {
        variable = `${singleVariable.system_variable}`;
      }
      return {
        title: singleVariable.title || singleVariable.system_variable,
        variable
      };
    });
    return concatVariables;
     */
    return globalVariables;
  }
);

export const isLoadingVariables = createSelector(
  [(state: RootState) => state?.variables.loadingVariables],
  loadingVariables => loadingVariables
);

export const isDeletingVariable = createSelector(
  [(state: RootState) => state?.variables.deletingVariable],
  deletingVariable => deletingVariable
);
